import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import Input from '../../../components/common/Input/Input';
import Button from '../../../components/common/Button/Button';
import { mockAuthService } from '../services/authService';
import './styles.css';

const anim = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// System alert component
const SystemAlert = ({ type = 'info', message, onClose }) => {
  if (!message) return null;

  const bgColor = {
    error: 'rgba(239, 68, 68, 0.1)',
    success: 'rgba(34, 197, 94, 0.1)',
    info: 'rgba(59, 130, 246, 0.1)',
    warning: 'rgba(245, 158, 11, 0.1)'
  }[type];

  const textColor = {
    error: '#ef4444',
    success: '#22c55e',
    info: '#3b82f6',
    warning: '#f59e0b'
  }[type];

  const IconComponent = type === 'error' ? AlertCircle : CheckCircle;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      style={{
        background: bgColor,
        border: `1px solid ${textColor}`,
        padding: '12px 16px',
        borderRadius: '6px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: textColor,
        fontSize: '14px'
      }}
    >
      <IconComponent size={18} />
      <span style={{ flex: 1 }}>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: '0',
            fontSize: '18px'
          }}
        >
          ×
        </button>
      )}
    </motion.div>
  );
};

// Password validation helper
const validatePassword = (password) => {
  const requirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  return requirements;
};

export const SetPasswordForm = ({ setView, activationToken }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [showRequirements, setShowRequirements] = useState(false);

  const passwordRequirements = validatePassword(password);
  const allRequirementsMet = Object.values(passwordRequirements).every((req) => req);

  const validateForm = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!allRequirementsMet) {
      newErrors.password = 'Password does not meet all requirements';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await mockAuthService.activateAccount(activationToken, password, confirmPassword);
      setAlert({ type: 'success', message: 'Password set successfully! Redirecting to login...' });
      setTimeout(() => {
        setView('signin');
      }, 1500);
    } catch (error) {
      setAlert({
        type: 'error',
        message: error.message || 'Failed to set password'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      <h2 style={{ color: 'white', marginBottom: '8px' }}>Create Password</h2>
      <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
        Enter a strong password for your account
      </p>

      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleSubmit}>
        {/* Password Input */}
        <div style={{ marginBottom: '20px' }}>
          <Input
            icon={Lock}
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setShowRequirements(true);
              if (errors.password) setErrors({ ...errors, password: '' });
            }}
            error={errors.password}
            showPasswordToggle={true}
            disabled={loading}
            aria-label="Password"
          />

          {/* Password Requirements */}
          {showRequirements && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              style={{
                marginTop: '10px',
                padding: '10px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#888'
              }}
            >
              <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Password Requirements:</p>
              <div style={{ display: 'grid', gap: '4px' }}>
                <div style={{ color: passwordRequirements.length ? '#22c55e' : '#888' }}>
                  ✓ At least 8 characters
                </div>
                <div style={{ color: passwordRequirements.uppercase ? '#22c55e' : '#888' }}>
                  ✓ At least one uppercase letter (A-Z)
                </div>
                <div style={{ color: passwordRequirements.lowercase ? '#22c55e' : '#888' }}>
                  ✓ At least one lowercase letter (a-z)
                </div>
                <div style={{ color: passwordRequirements.number ? '#22c55e' : '#888' }}>
                  ✓ At least one number (0-9)
                </div>
                <div style={{ color: passwordRequirements.special ? '#22c55e' : '#888' }}>
                  ✓ At least one special character (!@#$%^&*etc.)
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Confirm Password Input */}
        <Input
          icon={Lock}
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
          }}
          error={errors.confirmPassword}
          showPasswordToggle={true}
          disabled={loading}
          aria-label="Confirm password"
        />

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading || !allRequirementsMet}
          style={{
            opacity: !allRequirementsMet ? 0.5 : 1,
            cursor: !allRequirementsMet ? 'not-allowed' : 'pointer'
          }}
        >
          Set Password
        </Button>
      </form>

      {/* Helper Links */}
      <div className="terms-text" style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px',
        color: '#888',
        borderTop: '1px solid #333',
        paddingTop: '20px'
      }}>
        <p style={{ margin: '0' }}>
          Already have a password?{' '}
          <button
            onClick={() => setView('signin')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Sign in
          </button>
        </p>
      </div>
    </motion.div>
  );
};
